import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import usePagePermission from '../hooks/usePagePermission';
import { useAuth } from '../context/AuthContext';
import { usePermission } from '../context/PermissionContext';
import PageLoader from './PageLoader';

interface PagePermissionRouteProps {
  children: React.ReactNode;
  pageName: string;
  action?: 'read' | 'write' | 'delete';
  requireAuth?: boolean;
}

/**
 * Route wrapper that checks if user has permission to access a specific page
 * Redirects to /no-permission if user doesn't have the required permission
 */
const PagePermissionRoute: React.FC<PagePermissionRouteProps> = ({
  children,
  pageName,
  action = 'read',
  requireAuth = true,
}) => {
  const { user, isLoading: authLoading, isAuthenticated } = useAuth();
  const { isLoading: permissionLoading } = usePermission();
  const hasPermission = usePagePermission(pageName, action);
  const location = useLocation();
  const [permissionsReady, setPermissionsReady] = useState(false);

  useEffect(() => {
    const checkPermissionsReady = () => {
      if (user?.isclient) {
        setPermissionsReady(true);
        return;
      }

      if (user?.isadmin) {
        setPermissionsReady(true);
        return;
      }

      if (!authLoading && !permissionLoading) {
        setPermissionsReady(true);
      }
    };

    const timer = setTimeout(checkPermissionsReady, 100);
    return () => clearTimeout(timer);
  }, [user, authLoading, permissionLoading]);

  if (authLoading || permissionLoading || !permissionsReady) {
    return <PageLoader />;
  }

  if (requireAuth && !isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (user?.isclient) {
    return <>{children}</>;
  }

  if (user?.isadmin) {
    return <>{children}</>;
  }

  if (!hasPermission) {
    return (
      <Navigate
        to="/no-permission"
        state={{ from: location, pageName, action }}
        replace
      />
    );
  }

  return <>{children}</>;
};

export default PagePermissionRoute;
